/**
 * MultiSearchableSelect — the multi-value twin of SearchableSelect: the same
 * combobox trigger and searchable popover, with a checkbox per row.
 * For Media Devoted | Created By James Alexandrov
 *
 * Deliberately NOT MultiSelect: that one is an always-open inline list for
 * forms (pick target flows, pick rules). A filter bar needs a control that
 * collapses back to one trigger, which is what this adds.
 *
 * An EMPTY selection means "everything" — there is no 'all' sentinel option to
 * un-pick, so the filters compose as plain set membership.
 */

import { useMemo, useState, type FC } from 'react';
import * as Popover from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

import type { SearchableSelectOption } from '../SearchableSelect';
import { MultiSelectPanel } from './MultiSelectPanel';
import { MultiSelectTrigger } from './MultiSelectTrigger';

export interface MultiSearchableSelectProps {
  options: SearchableSelectOption<string>[];
  value: string[];
  onChange: (value: string[]) => void;
  /** Trigger text while nothing is picked ("All countries"). */
  placeholder: string;
  /** Plural noun for the many-picked summary ("3 countries"). */
  summaryNoun?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  /** Extra classes for the popover panel — e.g. widen it for long labels. */
  panelClassName?: string;
}

export const MultiSearchableSelect: FC<MultiSearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  summaryNoun,
  searchPlaceholder = 'Search...',
  disabled = false,
  className,
  panelClassName,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const picked = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value],
  );

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <Popover.Trigger asChild>
        <MultiSelectTrigger
          picked={picked}
          placeholder={placeholder}
          summaryNoun={summaryNoun}
          open={open}
          disabled={disabled}
          className={className}
        />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className={cn(
            'z-[250] w-[max(320px,var(--radix-popover-trigger-width))] min-w-[320px] max-w-[360px] rounded-lg border border-border bg-popover p-0 text-popover-foreground shadow-md',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            panelClassName,
          )}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <MultiSelectPanel
            options={options}
            value={value}
            onChange={onChange}
            search={search}
            onSearch={setSearch}
            searchPlaceholder={searchPlaceholder}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default MultiSearchableSelect;
